import {TableEditableState} from 'notebook/tables/core-ui/TableEditableState';
import type {TableMutationMethods} from 'notebook/tables/store/logic/mutations/createTableMutations';
import {
  computeCanDeleteTable,
  computeCanRenameTable,
} from 'notebook/tables/store/logic/TableModificationFlagsLogic';
import {
  createTableActiveCellSelector,
  TableActiveCellModel,
} from 'notebook/tables/store/selectors/tableModel/TableActiveCellSelectors';
import {
  createTableAffixedSelector,
  TableAffixedModel,
} from 'notebook/tables/store/selectors/tableModel/TableAffixedSelectors';
import {
  createTableColumnHeaderSelector,
  TableColumnHeaderModel,
} from 'notebook/tables/store/selectors/tableModel/TableColumnHeaderSelectors';
import {
  createTableContextSelector,
  TableContextModel,
} from 'notebook/tables/store/selectors/tableModel/TableContextSelectors';
import {
  createTableDimensionsSelector,
  TableDimensionsModel,
} from 'notebook/tables/store/selectors/tableModel/TableDimensionsSelectors';
import {
  createTableFillDownSelector,
  TableFillDownModel,
} from 'notebook/tables/store/selectors/tableModel/TableFillDownSelectors';
import {
  createTableLockingSelector,
  TableLockingModel,
} from 'notebook/tables/store/selectors/tableModel/TableLockingSelectors';
import {
  createTableMeasurementsSelector,
  TableMeasurementsModel,
} from 'notebook/tables/store/selectors/tableModel/TableMeasurementsSelectors';
import {
  createTableMergingSelector,
  TableMergingModel,
} from 'notebook/tables/store/selectors/tableModel/TableMergingSelectors';
import {
  createTableReorderingSelector,
  TableReorderingModel,
} from 'notebook/tables/store/selectors/tableModel/TableReorderingSelectors';
import {
  createTableSelectionSelector,
  TableSelectionModel,
} from 'notebook/tables/store/selectors/tableModel/TableSelectionSelectors';
import {
  createBoundaryIndexSelector,
  createTableSingleInputsSelector,
  TableSingleInputsModel,
} from 'notebook/tables/store/selectors/tableModel/TableSingleInputsSelectors';
import {
  createTableTitleSelector,
  TableTitleModel,
} from 'notebook/tables/store/selectors/tableModel/TableTitleSelectors';
import {
  createTableViewsSelector,
  TableViewsModel,
} from 'notebook/tables/store/selectors/tableModel/TableViewsSelectors';
import {
  createTableVisibilitySelector,
  TableVisibilityModel,
} from 'notebook/tables/store/selectors/tableModel/TableVisibilitySelectors';
import {
  createCountedDeepSelector,
  createTableStoreSelector,
} from 'notebook/tables/store/selectors/util/SelectorCreators';
import {
  TableEditableStableProps,
  TableEditableVolatileProps,
  TableEditableStore,
} from 'notebook/tables/store/TableStore';

/**
 * This model is what is passed down to the components, as well as to the RowModel.
 * This can contain data that is derived from the TableEditableStore.
 *
 * Ideally, logic from TableEditable should move into selectors and those selectors should generate values
 * that are stored on this object.
 */
export interface TableModel {
  props: TableEditableStableProps & TableEditableVolatileProps;
  state: TableEditableState;
  selection: TableSelectionModel;
  context: TableContextModel;
  dimensions: TableDimensionsModel;
  singleInputs: TableSingleInputsModel;
  locking: TableLockingModel;
  views: TableViewsModel;
  fillDown: TableFillDownModel;
  merging: TableMergingModel;
  activeCell: TableActiveCellModel;
  visibility: TableVisibilityModel;
  measurements: TableMeasurementsModel;
  reordering: TableReorderingModel;
  affixed: TableAffixedModel;
  columnHeader: TableColumnHeaderModel;
  title: TableTitleModel;

  /**
   * Whether the table can be deleted (a delete handler is available).
   */
  canDeleteTable: boolean;

  /**
   * Whether the table can be renamed.
   */
  canRenameTable: boolean;

  /**
   * The table's mutation methods, built once per table by `TableModelProvider`. Sub-models relay
   * this same instance so their consumers read it from the model they already subscribe to.
   */
  mutations: TableMutationMethods;
}

export function createVolatileTablePropsSelector(): (
  store: TableEditableStore
) => TableEditableVolatileProps {
  return createCountedDeepSelector('VolatileTableProps').withTypes<TableEditableStore>()(
    [(store) => store.volatileProps],
    (volatileProps): TableEditableVolatileProps => ({...volatileProps})
  );
}

export function createTableModelSelector(): (store: TableEditableStore) => TableModel {
  const volatileSelector = createVolatileTablePropsSelector();
  const tableContextSelector = createTableContextSelector(volatileSelector);
  const boundaryIndexSelector = createBoundaryIndexSelector(volatileSelector);
  const tableSelectionSelector = createTableSelectionSelector(volatileSelector, boundaryIndexSelector);
  const tableViewsSelector = createTableViewsSelector(volatileSelector, tableContextSelector);
  const tableDimensionsSelector = createTableDimensionsSelector(
    volatileSelector,
    tableContextSelector,
    tableSelectionSelector,
    tableViewsSelector
  );
  const tableLockingSelector = createTableLockingSelector(volatileSelector, tableSelectionSelector);
  const tableFillDownSelector = createTableFillDownSelector(
    tableSelectionSelector,
    volatileSelector,
    tableLockingSelector
  );
  const tableMergingSelector = createTableMergingSelector();
  const tableActiveCellSelector = createTableActiveCellSelector(
    volatileSelector,
    tableSelectionSelector,
    boundaryIndexSelector
  );
  const tableVisibilitySelector = createTableVisibilitySelector(tableSelectionSelector);
  const tableSingleInputsSelector = createTableSingleInputsSelector(
    volatileSelector,
    boundaryIndexSelector,
    tableDimensionsSelector,
    tableVisibilitySelector
  );
  const tableMeasurementsSelector = createTableMeasurementsSelector(
    tableViewsSelector,
    tableDimensionsSelector,
    boundaryIndexSelector,
    tableVisibilitySelector
  );
  const tableReorderingSelector = createTableReorderingSelector(tableDimensionsSelector);
  const tableAffixedSelector = createTableAffixedSelector();
  const tableColumnHeaderSelector = createTableColumnHeaderSelector(tableDimensionsSelector);
  const tableTitleSelector = createTableTitleSelector(tableContextSelector);

  return createTableStoreSelector(
    'tableModel',
    {
      volatileProps: volatileSelector,
      props: (store) => store.stableProps,
      state: (store) => store.state,
      selection: tableSelectionSelector,
      context: tableContextSelector,
      dimensions: tableDimensionsSelector,
      singleInputs: tableSingleInputsSelector,
      locking: tableLockingSelector,
      views: tableViewsSelector,
      fillDown: tableFillDownSelector,
      merging: tableMergingSelector,
      activeCell: tableActiveCellSelector,
      visibility: tableVisibilitySelector,
      measurements: tableMeasurementsSelector,
      reordering: tableReorderingSelector,
      affixed: tableAffixedSelector,
      columnHeader: tableColumnHeaderSelector,
      title: tableTitleSelector,
      canDeleteTable: (store) => computeCanDeleteTable(store.stableProps.deleteHandler),
      canRenameTable: (store) =>
        computeCanRenameTable(
          store.volatileProps.table._hideRename ?? false,
          store.stableProps.canRename ?? true
        ),
      mutations: (store) => store.mutations,
    },
    ({
      volatileProps,
      props,
      state,
      selection,
      context,
      dimensions,
      singleInputs,
      locking,
      views,
      fillDown,
      merging,
      activeCell,
      visibility,
      measurements,
      reordering,
      affixed,
      columnHeader,
      title,
      canDeleteTable,
      canRenameTable,
      mutations,
    }): TableModel => ({
      props: {
        ...props,
        ...volatileProps,
      },
      state,
      selection,
      context,
      dimensions,
      singleInputs,
      locking,
      views,
      fillDown,
      merging,
      activeCell,
      visibility,
      measurements,
      reordering,
      affixed,
      columnHeader,
      title,
      canDeleteTable,
      canRenameTable,
      mutations,
    })
  );
}
